import { useOrg } from '@/organization/useOrg';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  SubprojectBuildingDetails,
  TechnologyParameter,
  useSubprojectStore,
} from '../../stores/SubprojectStore.ts';
import { DecarbFooterBar } from '../components/DecarbFooterBar.tsx';
import { DecarbPageLayout } from '../components/DecarbPageLayout.tsx';
import { DecarbTabsPanel } from '../components/DecarbTabsPanel.tsx';
import { HelperText } from '../components/HelperText.tsx';
import { usePencilsDown } from '../hooks/usePencilsDown';
import {
  useAbattoirWithMetrics,
  useMedicalWithMetrics,
  useSportsArenaWithMetrics,
} from '../queries/specialized';
import { safelySaveSubproject } from '../saveHelpers.ts';
import { Tab, TabKinds } from '../types';
import {
  categorySelectionsMatch,
  createEmptyBuildingAlternative,
  createEmptySubprojectTechnology,
  storedCategoryCapitalCost,
  syncBuildingParamsAcrossAlternatives,
  updateTechParams,
} from '../utils.ts';
import { SpecializedForBuildingDetails } from './SpecializedForBuildingDetails.tsx';

// Every alternative exists and carries a 'specialized' tech entry, on a clone,
// before it becomes state (mutating state in place skips re-renders). Seeding
// the entry here -- not in an effect after load -- keeps the loaded state equal
// to the server copy, so it is not mistaken for an unsaved edit.
const withSpecializedDefaults = (
  subproject: SubprojectBuildingDetails,
): SubprojectBuildingDetails => {
  const updated = structuredClone(subproject);
  for (const alternativeTab of TabKinds) {
    if (!updated[alternativeTab]) {
      updated[alternativeTab] = createEmptyBuildingAlternative();
    }
    updated[alternativeTab].technologyParams ??= {};
    if (!updated[alternativeTab].technologyParams['specialized']) {
      updated[alternativeTab].technologyParams['specialized'] =
        createEmptySubprojectTechnology('specialized');
    }
  }
  return updated;
};

export const SpecializedSelection: React.FC = () => {
  const queryClient = useQueryClient();
  const subprojectStore = useSubprojectStore();
  const { subId } = useParams();
  const { selectedOrg } = useOrg();
  const [subproject, setSubproject] = useState<SubprojectBuildingDetails>();
  const [tab, setTab] = useState<Tab>('conventionalAlternative');
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  // The copy last handed to us by the server (on load, or as a save's result).
  // Same pattern as HVACSelection / LightingSelection.
  const [syncedSubproject, setSyncedSubproject] =
    useState<SubprojectBuildingDetails>();
  const costsArePending = !categorySelectionsMatch(
    subproject,
    syncedSubproject,
    'specialized',
  );
  const saveSeqRef = useRef(0);

  /**
   * Adopt what the backend handed back from a save: the stored costs only
   * exist after the server has run. See HVACSelection for the two guards.
   */
  const adoptSaveResult = useCallback(
    (
      savedSnapshot: SubprojectBuildingDetails,
      result: SubprojectBuildingDetails,
      seq: number,
    ) => {
      if (seq !== saveSeqRef.current) return;
      const refreshed = withSpecializedDefaults(result);
      setSyncedSubproject(refreshed);
      setSubproject((current) =>
        current === savedSnapshot ? refreshed : current,
      );
    },
    [],
  );
  const pencilsDown = usePencilsDown();

  // Determine which types of specialized equipment to show based on building usage
  const equipmentTypes = useMemo(() => {
    const buildingUsages = subproject?.buildingUsages || [];
    const types: string[] = [];

    if (buildingUsages.includes('Food/Agriculture')) {
      types.push('abattoir');
    }
    if (buildingUsages.includes('Recreation')) {
      types.push('sports');
    }
    if (buildingUsages.includes('Medical')) {
      types.push('medical');
    }

    return types;
  }, [subproject?.buildingUsages]);
  const conditionedSpace =
    subproject?.conventionalAlternative?.buildingParams?.conditionedSpace;

  // Fetch appropriate equipment based on building types
  const { data: sportsEquipment } = useSportsArenaWithMetrics(
    equipmentTypes.includes('sports') ? conditionedSpace : undefined,
  );

  const { data: abattoirEquipment } = useAbattoirWithMetrics(
    equipmentTypes.includes('abattoir') ? conditionedSpace : undefined,
  );

  const { data: medicalEquipment } = useMedicalWithMetrics(
    equipmentTypes.includes('medical') ? conditionedSpace : undefined,
  );

  // Combine all applicable equipment data
  const specializedEquipment = useMemo(
    () => [
      ...(equipmentTypes.includes('sports') && sportsEquipment
        ? sportsEquipment
        : []),
      ...(equipmentTypes.includes('abattoir') && abattoirEquipment
        ? abattoirEquipment
        : []),
      ...(equipmentTypes.includes('medical') && medicalEquipment
        ? medicalEquipment
        : []),
    ],
    [equipmentTypes, sportsEquipment, abattoirEquipment, medicalEquipment],
  );

  // Fetch subproject from database and initialize alternatives if needed
  useEffect(() => {
    if (!subId || !selectedOrg) {
      return;
    }

    subprojectStore
      .fetchSubprojectDetails(subId, selectedOrg.id)
      .then((subproject) => {
        console.log('=== FETCHED SUBPROJECT ===');
        console.log('Specialized data from backend:', {
          conventional:
            subproject?.conventionalAlternative?.technologyParams?.[
              'specialized'
            ],
          reduced:
            subproject?.reducedCarbonAlternative?.technologyParams?.[
              'specialized'
            ],
          zero: subproject?.zeroCarbonAlternative?.technologyParams?.[
            'specialized'
          ],
        });

        const updatedSubproject = withSpecializedDefaults(subproject);
        setSubproject(updatedSubproject);
        setSyncedSubproject(updatedSubproject);
        return updatedSubproject;
      })
      .catch(console.error);
  }, [subId, subprojectStore, selectedOrg]);

  // Debounced auto-save after selection changes
  useEffect(() => {
    if (pencilsDown) return;
    if (!subproject || !subId || !selectedOrg) return;

    // Nothing has changed since the server last spoke: covers the initial load
    // and a state adopted from a save result (saving that would loop).
    if (subproject === syncedSubproject) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const savedSnapshot = subproject;
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      const seq = ++saveSeqRef.current;
      safelySaveSubproject(subId, selectedOrg.id, savedSnapshot, queryClient)
        .then((result) => adoptSaveResult(savedSnapshot, result, seq))
        .catch(console.error)
        .finally(() => setIsSaving(false));
    }, 500);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    pencilsDown,
    subproject,
    syncedSubproject,
    subId,
    selectedOrg,
    queryClient,
    adoptSaveResult,
  ]);

  // Handle increment, decrement and change of equipment counts
  const handleChangeEquipmentCount = useCallback(
    (equipmentId: string, newCount: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (
          !previousSubproject?.[tab]?.technologyParams?.['specialized']?.params
        ) {
          return;
        }

        // Find the equipment to check its limit
        const equipment = specializedEquipment?.find(
          (e) => e.id === equipmentId,
        );
        if (!equipment) {
          return;
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'specialized',
          (params) => {
            if (newCount > 0) {
              return params.map((equipmentSystem) =>
                equipmentSystem.techId === equipmentId
                  ? { ...equipmentSystem, units: newCount }
                  : equipmentSystem,
              );
            } else {
              return params.filter(
                (equipmentSystem: TechnologyParameter) =>
                  equipmentSystem.techId !== equipmentId,
              );
            }
          },
        );
      });
    },
    [tab, specializedEquipment],
  );

  // Add equipment to list of selected equipment
  const handleAddEquipment = useCallback(
    (equipmentId: string, limit: number) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject) {
          return;
        }

        if (
          !previousSubproject?.[tab]?.technologyParams?.['specialized']?.params
        ) {
          return;
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'specialized',
          (params) => [...params, { techId: equipmentId, units: limit }],
        );
      });
    },
    [tab],
  );

  // Copy selected equipment from the previous alternative
  const handleCopyEquipmentSettings = useCallback(
    (tab: Tab) => {
      setSubproject((previousSubproject) => {
        if (!previousSubproject || specializedEquipment.length === 0) {
          return;
        }

        // Don't copy if already on conventional
        if (tab === 'conventionalAlternative') {
          return;
        }

        // Determine which alternative to copy FROM
        let tabToCopyFrom: Tab = 'conventionalAlternative';
        if (tab === 'zeroCarbonAlternative') {
          tabToCopyFrom = 'reducedCarbonAlternative';
        }

        const equipmentList: TechnologyParameter[] =
          previousSubproject[tabToCopyFrom].technologyParams['specialized']
            ?.params || [];

        if (equipmentList.length === 0) {
          return;
        }

        // Filter equipment based on target alternative
        const filteredEquipmentList = equipmentList.filter((equipmentParam) => {
          const equipment = specializedEquipment.find(
            (e) => e.id === equipmentParam.techId,
          );
          if (!equipment) {
            console.error(
              'Unable to locate equipment during copy, not found in storage',
            );
            return false;
          }

          // Low Carbon: No gas
          if (tab === 'reducedCarbonAlternative') {
            if (equipment.energySink?.includes('Gas')) {
              return false;
            }
          }

          // Net Zero: No gas AND only low-energy electricity (≤2 icons)
          if (tab === 'zeroCarbonAlternative') {
            if (equipment.energySink?.includes('Gas')) {
              return false;
            }
            if (
              equipment.energySink === 'Electricity' &&
              equipment.relativeEnergyDemand > 2
            ) {
              return false;
            }
          }

          return true;
        });

        const excludedCount =
          equipmentList.length - filteredEquipmentList.length;
        if (excludedCount > 0) {
          console.log(
            `[Copy] ${excludedCount} item(s) excluded during copy to ${tab} for specialized`,
          );
        }

        return updateTechParams(
          previousSubproject,
          tab,
          'specialized',
          () => filteredEquipmentList,
        );
      });
    },
    [specializedEquipment],
  );

  const getCostAndEnergyMetrics = useCallback(
    (tab: Tab) => {
      const alternative = subproject?.[tab];
      const params = alternative?.technologyParams['specialized']?.params;
      if (!params || specializedEquipment.length === 0) {
        return undefined;
      }
      const metrics = params.reduce<{ energyDemand: number }>(
        (a, b) => {
          const equipment = specializedEquipment.find((e) => e.id === b.techId);
          if (!equipment) {
            console.error('Unable to locate equipment, not found in storage');
            return a;
          }
          a.energyDemand += Number(equipment.energyUsePerYr) * b.units;
          return a;
        },
        { energyDemand: 0 },
      );
      // Capital comes from the stored category total, never local arithmetic
      // -- see storedCategoryCapitalCost for why.
      const cost = storedCategoryCapitalCost(
        alternative,
        'specialized',
        costsArePending,
      );
      if (!cost && metrics.energyDemand === 0) {
        return undefined;
      }
      return { energyDemand: metrics.energyDemand, cost };
    },
    [subproject, specializedEquipment, costsArePending],
  );

  const conventionalMetrics = useMemo(
    () => getCostAndEnergyMetrics('conventionalAlternative'),
    [getCostAndEnergyMetrics],
  );
  const reducedCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('reducedCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );
  const zeroCarbonMetrics = useMemo(
    () => getCostAndEnergyMetrics('zeroCarbonAlternative'),
    [getCostAndEnergyMetrics],
  );

  if (!selectedOrg) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant={'h5'}>
          Please select an organization from the user menu to access this
          project.
        </Typography>
      </Box>
    );
  }

  // Show message if no specialized equipment is available for this building type
  if (equipmentTypes.length === 0) {
    return (
      <DecarbPageLayout
        title={'select Specialized Equipment'}
        tabs={
          <DecarbTabsPanel
            currentTab={tab}
            onClick={setTab}
            conventionalMetrics={conventionalMetrics}
            reducedCarbonMetrics={reducedCarbonMetrics}
            zeroCarbonMetrics={zeroCarbonMetrics}
          />
        }
        footer={
          <DecarbFooterBar
            currentTab={tab}
            changeTab={setTab}
            saveSubproject={(onSuccessFn: () => void) => {
              if (pencilsDown) {
                onSuccessFn();
                return;
              }
              if (subproject && subId && selectedOrg) {
                const seq = ++saveSeqRef.current;
                safelySaveSubproject(
                  subId,
                  selectedOrg.id,
                  subproject,
                  queryClient,
                )
                  .then((result) => {
                    adoptSaveResult(subproject, result, seq);
                    onSuccessFn();
                  })
                  .catch((error) => {
                    console.error('Failed to save specialized data:', error);
                    alert('Failed to save changes. Please try again.');
                  });
              }
            }}
          />
        }
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant='h6' color='text.secondary'>
            No specialized equipment available for this building type.
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Specialized equipment is only available for buildings with
            Food/Agriculture, Recreation, or Medical usage types.
          </Typography>
        </Box>
      </DecarbPageLayout>
    );
  }

  return (
    <DecarbPageLayout
      title={'select Specialized Equipment'}
      tabs={
        <DecarbTabsPanel
          currentTab={tab}
          onClick={setTab}
          conventionalMetrics={conventionalMetrics}
          reducedCarbonMetrics={reducedCarbonMetrics}
          zeroCarbonMetrics={zeroCarbonMetrics}
        />
      }
      footer={
        <DecarbFooterBar
          currentTab={tab}
          changeTab={setTab}
          saveSubproject={(onSuccessFn: () => void) => {
            if (pencilsDown) {
              onSuccessFn();
              return;
            }
            if (subproject) {
              console.log('=== SAVING SPECIALIZED ===');
              console.log('Subproject before sync:', subproject);
              // Sync building params before saving to ensure all alternatives have the same measurements
              const withSyncedParams =
                syncBuildingParamsAcrossAlternatives(subproject);
              console.log('Synced subproject:', withSyncedParams);
              console.log('Specialized params:', {
                conventional:
                  withSyncedParams.conventionalAlternative?.technologyParams?.[
                    'specialized'
                  ],
                reduced:
                  withSyncedParams.reducedCarbonAlternative?.technologyParams?.[
                    'specialized'
                  ],
                zero: withSyncedParams.zeroCarbonAlternative
                  ?.technologyParams?.['specialized'],
              });
              if (subId && selectedOrg) {
                const seq = ++saveSeqRef.current;
                safelySaveSubproject(
                  subId,
                  selectedOrg.id,
                  withSyncedParams,
                  queryClient,
                )
                  .then((result) => {
                    adoptSaveResult(subproject, result, seq);
                    console.log('Save successful!');
                    onSuccessFn();
                  })
                  .catch((error) => {
                    console.error('Failed to save specialized data:', error);
                    alert('Failed to save changes. Please try again.');
                  });
              }
            }
          }}
        />
      }
    >
      {subproject && specializedEquipment.length > 0 && (
        <SpecializedForBuildingDetails
          subproject={subproject}
          costsArePending={costsArePending}
          tab={tab}
          specializedEquipment={specializedEquipment}
          onAddEquipment={handleAddEquipment}
          onChangeEquipmentCount={handleChangeEquipmentCount}
          onCopyConventionalAlternative={handleCopyEquipmentSettings}
          isLoading={isSaving}
          readOnly={pencilsDown}
          emptyText={
            pencilsDown ? undefined : (
              <HelperText
                tab={tab}
                onCopySelection={() => handleCopyEquipmentSettings(tab)}
              />
            )
          }
        />
      )}
    </DecarbPageLayout>
  );
};
